import type { ReportCatalog, ReportQuery } from "@gettactic/api";

export type RelatedQuery = {
  label: string;
  query: ReportQuery;
};

const MAX_RELATED = 4;

/** Measures that answer the natural follow-up to another one. */
const PARTNERS: Record<string, { measure: string; label: string }> = {
  occupancy: {
    measure: "no_shows",
    label: "How many were cancelled for a missing check-in?"
  },
  no_shows: {
    measure: "occupancy",
    label: "How does occupancy look for the same cut?"
  },
  reservations: {
    measure: "checkin_rate",
    label: "How many of those checked in?"
  },
  checkin_rate: {
    measure: "reservations",
    label: "How many reservations is that?"
  },
  room_hours: {
    measure: "reservations",
    label: "How many bookings is that?"
  },
  requests: {
    measure: "time_to_resolve",
    label: "How long do these take to resolve?"
  },
  resolved: { measure: "requests", label: "How many were filed meanwhile?" },
  time_to_resolve: {
    measure: "requests",
    label: "How many requests is that?"
  },
  visits: { measure: "invitations", label: "How many were invited ahead?" },
  invitations: { measure: "visits", label: "How many actually came?" },
  remote_days: {
    measure: "vacation_days",
    label: "And vacation days for the same cut?"
  }
};

const dimensionLabel = (catalog: ReportCatalog, key: string): string =>
  catalog.dimensions.find((d) => d.key === key)?.label.toLowerCase() ?? key;

/**
 * Next questions worth asking from the current one: the same cut by another
 * dimension, crossed with a second one, compared, its partner measure, or
 * zoomed out to the year. Only dimensions the measure supports and that are
 * not already used are offered.
 */
export const relatedQueries = (
  catalog: ReportCatalog,
  query: ReportQuery
): RelatedQuery[] => {
  const measure = catalog.measures.find((m) => m.key === query.measure);
  if (!measure) {
    return [];
  }
  const breakdown = query.breakdown ?? [];
  const used = new Set([
    ...breakdown,
    ...(query.filters ?? []).map((f) => f.dimension)
  ]);
  const free = measure.dimensions.filter((d) => !used.has(d));
  const out: RelatedQuery[] = [];

  if (breakdown.length === 1 && free.length > 0) {
    out.push({
      label: `Same thing by ${dimensionLabel(catalog, free[0])}`,
      query: { ...query, breakdown: [free[0]] }
    });
  }
  if (breakdown.length <= 1 && free.length > 0) {
    const cross = free.includes("weekday")
      ? "weekday"
      : (free.find((d) => d !== free[0]) ?? free[0]);
    const both =
      breakdown.length === 1
        ? [breakdown[0], cross]
        : [free.find((d) => d !== cross) ?? cross, cross];
    if (both[0] !== both[1]) {
      out.push({
        label: `Cross it with ${dimensionLabel(catalog, cross)}`,
        query: { ...query, breakdown: both }
      });
    }
  }
  if (
    breakdown.length === 0 &&
    measure.dimensions.includes("weekday") &&
    measure.dimensions.includes("hour") &&
    !used.has("weekday") &&
    !used.has("hour")
  ) {
    out.push({
      label: "When in the week does this happen?",
      query: { ...query, breakdown: ["weekday", "hour"] }
    });
  }
  if (query.compare !== "previous_period") {
    out.push({
      label: "Compare with the previous period",
      query: { ...query, compare: "previous_period" }
    });
  } else {
    out.push({
      label: "Compare with the same period last year",
      query: { ...query, compare: "same_period_last_year" }
    });
  }
  const partner = PARTNERS[query.measure];
  const partnerMeasure = partner
    ? catalog.measures.find((m) => m.key === partner.measure)
    : undefined;
  if (partner && partnerMeasure && !partnerMeasure.hidden) {
    const allowed = new Set(partnerMeasure.dimensions);
    out.push({
      label: partner.label,
      query: {
        ...query,
        measure: partner.measure,
        breakdown: breakdown.filter((d) => allowed.has(d)),
        filters: (query.filters ?? []).filter((f) => allowed.has(f.dimension))
      }
    });
  }
  if (query.period.preset !== "year_to_date") {
    out.push({
      label: "Zoom out to the whole year",
      query: { ...query, period: { preset: "year_to_date" } }
    });
  }
  return out.slice(0, MAX_RELATED);
};
